import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import FormField from '../FormField';
import { Loader2, Plus, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { dryingCheckSchema } from '@/schema/drying-lot-schema';
import { useParams } from 'react-router-dom';
import { addDryingCheck, deleteMedia, updateDryingCheck } from '@/network/api.service';
import { toast } from 'sonner';
import MediaUploader from '../lot/MediaUploader';
import { useMediaUploader } from '@/hooks/useMediaUploader';
// import { useModalStore } from '@/hooks/useModalStore';

const AddDryingCheckDrawer = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onClose,
  disabled,
  initialData,
  setEditingItem,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { drying_lot_id } = useParams();
  const [mediaToRemove, setMediaToRemove] = useState([]);

  const uploadRef = useRef(null);
  const media = useMediaUploader({ folder: 'lot-drying-check-media' });
  // const openModal = useModalStore(s => s.openModal);

  // Use controlled open if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(dryingCheckSchema),
    defaultValues: {
      time_of_check: new Date(),
      was_coffee_raked: null,
      moisture: null,
      temperature: '',
      notes: '',
      media: [],
    },
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'media',
  });

  // Reset form when initialData changes (when editing)
  useEffect(() => {
    if (initialData && open) {
      reset({
        time_of_check: initialData.time_of_check ? new Date(initialData.time_of_check) : new Date(),
        was_coffee_raked: initialData.was_coffee_raked ?? false,
        moisture: initialData.moisture ?? null,
        temperature: initialData.temperature ?? '',
        notes: initialData.notes ?? '',
        media: initialData.media ?? [],
      });
    }
  }, [initialData, open, reset]);

  useEffect(() => {
    if (!open) {
      reset({
        time_of_check: new Date(),
        was_coffee_raked: null,
        moisture: null,
        temperature: '',
        notes: '',
        media: [],
      });
      media.reset();
      setEditingItem?.(null);
    }
  }, [open, reset, setEditingItem]);

  const onSubmit = async data => {
    setLoading(true);

    if (mediaToRemove.length > 0) {
      await deleteMedia(mediaToRemove.map(m => m.path));
    }

    if (uploadRef.current) {
      await uploadRef.current();
    }
    const uploaded = await media.uploadAll();
    const existingMedia = Array.isArray(data.media) ? data.media : [];

    const mergedMedia = uploaded && uploaded.length > 0 ? [...existingMedia, ...uploaded] : existingMedia;

    const payload = {
      drying_lot_id: drying_lot_id,
      ...data,
      media: mergedMedia,
    };

    if (initialData) {
      payload.check_id = initialData._id;
    }

    try {
      const apiCall = initialData ? updateDryingCheck : addDryingCheck;
      const { data: responseData } = await apiCall(payload);

      if (responseData?.success) {
        toast.success(responseData?.message);
        reset();
        handleOpenChange(false);
        setMediaToRemove([]);
        media.reset();
        if (onClose) {
          onClose(true); // Pass true to indicate successful save
        }
      }
    } catch (error) {
      // console.log(error);

      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = newOpen => {
    setOpen(newOpen);

    if (!newOpen && onClose) {
      onClose();
    }

    if (newOpen) {
      // Blur any focused elements in the background when drawer opens
      const activeElement = document.activeElement;
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
  };

  const onRemoveMedia = file => {
    setMediaToRemove(prev => [...prev, file]);
    const index = fields.findIndex(m => m._id === file._id);

    if (index !== -1) {
      remove(index);
    }
  };

  const handleAddClick = () => {
    if (onClose) {
      onClose();
    }
    setOpen(true);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="border-dashed" onClick={handleAddClick} disabled={disabled}>
          <Plus /> Add check
        </Button>
      </DrawerTrigger>

      <DrawerContent className="max-h-[90vh]">
        <form tabIndex={-1} onSubmit={handleSubmit(onSubmit)} className="flex h-full flex-col overflow-y-auto">
          <DrawerHeader className="text-left">
            <DrawerTitle className="p-1">Record drying check</DrawerTitle>
            <DrawerDescription className="hidden"></DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-1 flex-col gap-3 px-5 pb-5">
            <FormField
              datetime
              label="Time of check"
              name="time_of_check"
              required
              control={control}
              disabled={loading}
              errors={errors}
            />
            <FormField
              select
              label="Was coffee raked?"
              name="was_coffee_raked"
              required
              control={control}
              disabled={loading}
              placeholder="Select"
              className="bg-transparent"
              errors={errors}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
            />
            <FormField
              type="number"
              label="Moisture %"
              name="moisture"
              step="0.01"
              placeholder="e.g., 4.2"
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              type="number"
              label="Bean temperature"
              name="temperature"
              step="0.01"
              placeholder="e.g., 28.5"
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            <FormField
              textarea
              label="Notes"
              name="notes"
              placeholder="Enter notes here..."
              className="bg-transparent"
              register={register}
              disabled={loading}
              errors={errors}
            />
            {/* Later add media upload here */}
            <MediaUploader
              media={media}
              label="Photos/Videos"
              folder={'lot-process-media'}
              onUploaded={fn => (uploadRef.current = fn)}
              className="bg-transparent"
              loading={loading}
            >
              {fields?.map((m, idx) => (
                <div key={idx} className="relative h-[72px] w-[72px] overflow-hidden rounded-xl">
                  {m.mime_type === 'image' ? (
                    <img src={m.public_url} className="h-full w-full object-cover" />
                  ) : (
                    <video src={m.public_url} className="h-full w-full object-cover" muted playsInline />
                  )}

                  <button
                    type="button"
                    onClick={() => onRemoveMedia(m)}
                    className="absolute right-1 top-1 rounded-full bg-white/80 p-0.5"
                    disabled={loading}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </MediaUploader>
          </div>

          <DrawerFooter className="flex flex-row justify-end gap-2 border-t">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spinSlow" />}
              {loading ? 'Finishing...' : 'Save Check'}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default AddDryingCheckDrawer;
